import type { SendbirdGroupChannel, SendbirdMessage } from '@sendbird/uikit-utils';
import { CHAT_PREVIEW_LOOKBACK } from '../constants';
import type { TPreviewMessageResolution } from '../types';
import { isWelcomeMessage } from './welcomeMessage';

const cache = new Map<string, SendbirdMessage | null>();

const keyOf = (channel: SendbirdGroupChannel, last: SendbirdMessage) => `${channel.url}:${last.messageId}`;

export const resolvePreviewMessageSync = (channel: SendbirdGroupChannel): TPreviewMessageResolution => {
    const last = channel.lastMessage;
    if (!last || isWelcomeMessage(last)) return { message: null, needsLookup: false };
    if (!last.isAdminMessage()) return { message: last, needsLookup: false };
    const key = keyOf(channel, last);
    if (cache.has(key)) return { message: cache.get(key) ?? null, needsLookup: false };
    return { message: null, needsLookup: true };
};

export const lookupPreviewMessage = async (channel: SendbirdGroupChannel): Promise<SendbirdMessage | null> => {
    const last = channel.lastMessage;
    if (!last) return null;
    const key = keyOf(channel, last);
    if (cache.has(key)) return cache.get(key) ?? null;

    let found: SendbirdMessage | null = null;
    try {
        const messages = await channel.getMessagesByTimestamp(last.createdAt, {
            prevResultSize: CHAT_PREVIEW_LOOKBACK,
            nextResultSize: 0,
            isInclusive: false,
        });
        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i] as SendbirdMessage;
            if (!message.isAdminMessage()) {
                found = message;
                break;
            }
        }
    } catch {
        return null;
    }
    cache.set(key, found);
    return found;
};
